import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Helmet} from 'react-helmet-async';

import {Box} from '@spothero/ui';

import {Page} from 'utils/page-utils';
import {formatSlices} from 'utils/cms';
import useCanonical from 'hooks/use-canonical';
import AppRatingSchemaJsonLd from 'common/app-rating-schema-jsonld';
import trackMarketingLanding from 'segment/pages/marketing-landing';
import FAQPageStructuredData from 'cms/seo/faq-page-structured-data';
import WithSsrRedirectTrailingSlash from 'router/WithSsrRedirectTrailingSlash';

const DefaultMarketingPage = () => {
    // Returned Values from Prismic
    const {
        body: slices,
        meta_title_marketing: metaTitleMarketing,
        meta_description_marketing: metaDescriptionMarketing,
        opengraph_title: openGraphTitle,
        opengraph_image: openGraphImage,
        opengraph_description: openGraphDescription,
        pageType,
    } = useSelector(state => state.page);
    const {jobs} = useSelector(state => state.jobs.data);
    // Open Graph Conditionals
    const ogTitle = openGraphTitle || metaTitleMarketing;
    const ogDescription = openGraphDescription || metaDescriptionMarketing;
    const {canonicalURL, metaTag} = useCanonical();

    useEffect(() => {
        trackMarketingLanding({
            pageType: Page.MARKETING,
            page: canonicalURL,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="MarketingPage">
            <Helmet>
                <title>{metaTitleMarketing}</title>
                {metaTag}
                {/* ---------------------------------------------------------- *\
                        In order for the user-entered meta description to
                        override the default meta description created within
                        the ApplicationMeta file, this description meta tag must
                        have the same attributes as the default (name, itemProp,
                        and content).
                \* ---------------------------------------------------------- */}
                <meta
                    name="description"
                    itemProp="description"
                    content={metaDescriptionMarketing}
                />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:url" content={canonicalURL} />
                {openGraphImage ? (
                    <meta property="og:image" content={openGraphImage} />
                ) : null}
                <meta property="og:type" itemProp="og:type" content="website" />

                <body className="standalone-page marketing-page" />
            </Helmet>

            <AppRatingSchemaJsonLd />
            <FAQPageStructuredData slices={slices} />
            <Box as="main">{formatSlices({slices, pageType, jobs})}</Box>
        </div>
    );
};

export default WithSsrRedirectTrailingSlash(DefaultMarketingPage);
